/**
 * FloorPlanAligner Component
 * Interactive tool for aligning floor plan overlays on the map
 *
 * Features:
 * - Drag center to move entire floor plan
 * - Drag corners to scale (maintains aspect ratio with Shift)
 * - Drag rotation handle to rotate
 * - Real-time preview on map
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
import type { FloorPlanTransform } from '../../../../shared/types/floorplan';
import './FloorPlanAligner.css';

interface FloorPlanAlignerProps {
  /** Cesium viewer instance */
  viewer: Cesium.Viewer;
  /** Image URL or data URL for the floor plan */
  imageUrl: string;
  /** Image dimensions */
  imageWidth: number;
  imageHeight: number;
  /** Initial transform (or default if none) */
  initialTransform: FloorPlanTransform;
  /** Callback when alignment is confirmed */
  onConfirm: (transform: FloorPlanTransform) => void;
  /** Callback when alignment is cancelled */
  onCancel: () => void;
}

type DragMode = 'none' | 'move' | 'scale-nw' | 'scale-ne' | 'scale-sw' | 'scale-se' | 'rotate';

export function FloorPlanAligner({
  viewer,
  imageUrl,
  imageWidth,
  imageHeight,
  initialTransform,
  onConfirm,
  onCancel,
}: FloorPlanAlignerProps) {
  const [transform, setTransform] = useState<FloorPlanTransform>(initialTransform);
  const [dragMode, setDragMode] = useState<DragMode>('none');
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [initialDragTransform, setInitialDragTransform] = useState<FloorPlanTransform | null>(null);

  const overlayEntityRef = useRef<Cesium.Entity | null>(null);
  const handleEntitiesRef = useRef<Cesium.Entity[]>([]);
  const handlerRef = useRef<Cesium.ScreenSpaceEventHandler | null>(null);

  // Refs to track state in event handlers (closures capture stale state otherwise)
  const dragModeRef = useRef<DragMode>('none');
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const initialDragTransformRef = useRef<FloorPlanTransform | null>(null);
  const transformRef = useRef<FloorPlanTransform>(initialTransform);

  // Keep refs in sync with state
  useEffect(() => {
    dragModeRef.current = dragMode;
  }, [dragMode]);

  useEffect(() => {
    dragStartRef.current = dragStart;
  }, [dragStart]);

  useEffect(() => {
    initialDragTransformRef.current = initialDragTransform;
  }, [initialDragTransform]);

  useEffect(() => {
    transformRef.current = transform;
  }, [transform]);

  // Calculate image bounds from transform
  const calculateBounds = useCallback((t: FloorPlanTransform) => {
    const halfWidthMeters = (imageWidth * t.scaleX) / 2;
    const halfHeightMeters = (imageHeight * t.scaleY) / 2;

    // Convert meters to degrees at anchor latitude
    const metersPerDegreeLat = 111320;
    const metersPerDegreeLng = 111320 * Math.cos(t.anchorLat * Math.PI / 180);

    const halfWidthDeg = halfWidthMeters / metersPerDegreeLng;
    const halfHeightDeg = halfHeightMeters / metersPerDegreeLat;

    return {
      west: t.anchorLng - halfWidthDeg,
      south: t.anchorLat - halfHeightDeg,
      east: t.anchorLng + halfWidthDeg,
      north: t.anchorLat + halfHeightDeg,
    };
  }, [imageWidth, imageHeight]);

  // Update overlay entity with current transform
  const updateOverlay = useCallback(() => {
    if (!viewer || viewer.isDestroyed()) return;

    const bounds = calculateBounds(transform);

    // Remove existing overlay
    if (overlayEntityRef.current) {
      viewer.entities.remove(overlayEntityRef.current);
    }

    // Create ground overlay with rotation
    const rectangle = Cesium.Rectangle.fromDegrees(
      bounds.west,
      bounds.south,
      bounds.east,
      bounds.north
    );

    overlayEntityRef.current = viewer.entities.add({
      rectangle: {
        coordinates: rectangle,
        material: new Cesium.ImageMaterialProperty({
          image: imageUrl,
          transparent: true,
        }),
        rotation: Cesium.Math.toRadians(-transform.rotation),
        stRotation: Cesium.Math.toRadians(-transform.rotation),
      },
    });

    // Update handle positions
    updateHandles();
  }, [viewer, transform, imageUrl, calculateBounds]);

  // Update handle entity positions
  const updateHandles = useCallback(() => {
    if (!viewer || viewer.isDestroyed()) return;

    // Clear existing handles
    handleEntitiesRef.current.forEach(e => viewer.entities.remove(e));
    handleEntitiesRef.current = [];

    const bounds = calculateBounds(transform);
    const centerLng = transform.anchorLng;
    const centerLat = transform.anchorLat;

    // Corner positions (before rotation)
    const corners = [
      { id: 'nw', lng: bounds.west, lat: bounds.north },
      { id: 'ne', lng: bounds.east, lat: bounds.north },
      { id: 'sw', lng: bounds.west, lat: bounds.south },
      { id: 'se', lng: bounds.east, lat: bounds.south },
    ];

    // Apply rotation to corner positions
    const rotatedCorners = corners.map(corner => {
      const dx = corner.lng - centerLng;
      const dy = corner.lat - centerLat;
      const angle = Cesium.Math.toRadians(-transform.rotation);
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      return {
        id: corner.id,
        lng: centerLng + dx * cos - dy * sin,
        lat: centerLat + dx * sin + dy * cos,
      };
    });

    // Add corner handles
    rotatedCorners.forEach(corner => {
      const entity = viewer.entities.add({
        position: Cesium.Cartesian3.fromDegrees(corner.lng, corner.lat),
        point: {
          pixelSize: 14,
          color: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.fromCssColorString('#4F7CFF'),
          outlineWidth: 3,
        },
        properties: {
          handleType: `scale-${corner.id}`,
        },
      });
      handleEntitiesRef.current.push(entity);
    });

    // Add center handle (for moving)
    const centerEntity = viewer.entities.add({
      position: Cesium.Cartesian3.fromDegrees(centerLng, centerLat),
      point: {
        pixelSize: 16,
        color: Cesium.Color.fromCssColorString('#4F7CFF'),
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 3,
      },
      properties: {
        handleType: 'move',
      },
    });
    handleEntitiesRef.current.push(centerEntity);

    // Add rotation handle (above center)
    const rotHandleOffset = (bounds.north - bounds.south) * 0.6;
    const rotHandleLng = centerLng;
    const rotHandleLat = centerLat + rotHandleOffset;

    // Rotate the rotation handle position
    const dx = rotHandleLng - centerLng;
    const dy = rotHandleLat - centerLat;
    const angle = Cesium.Math.toRadians(-transform.rotation);
    const rotatedRotLng = centerLng + dx * Math.cos(angle) - dy * Math.sin(angle);
    const rotatedRotLat = centerLat + dx * Math.sin(angle) + dy * Math.cos(angle);

    const rotEntity = viewer.entities.add({
      position: Cesium.Cartesian3.fromDegrees(rotatedRotLng, rotatedRotLat),
      point: {
        pixelSize: 12,
        color: Cesium.Color.fromCssColorString('#ff6432'),
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 2,
      },
      properties: {
        handleType: 'rotate',
      },
    });
    handleEntitiesRef.current.push(rotEntity);

    // Add line from center to rotation handle
    const lineEntity = viewer.entities.add({
      polyline: {
        positions: Cesium.Cartesian3.fromDegreesArray([
          centerLng, centerLat,
          rotatedRotLng, rotatedRotLat,
        ]),
        width: 2,
        material: Cesium.Color.fromCssColorString('#ff6432').withAlpha(0.7),
      },
    });
    handleEntitiesRef.current.push(lineEntity);

  }, [viewer, transform, calculateBounds]);

  // Initialize overlay and handlers
  useEffect(() => {
    if (!viewer || viewer.isDestroyed()) return;

    updateOverlay();

    // Set up event handlers
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    handlerRef.current = handler;

    // Mouse down - start drag
    handler.setInputAction((event: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      const pickedObject = viewer.scene.pick(event.position);

      if (Cesium.defined(pickedObject) && pickedObject.id?.properties?.handleType) {
        const handleType = pickedObject.id.properties.handleType.getValue() as DragMode;
        setDragMode(handleType);
        setDragStart({ x: event.position.x, y: event.position.y });
        setInitialDragTransform({ ...transformRef.current });
      }
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

    // Mouse move - update drag
    handler.setInputAction((event: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
      // Read from refs to get current values (closures would have stale state)
      const currentDragMode = dragModeRef.current;
      const currentDragStart = dragStartRef.current;
      const currentInitialDragTransform = initialDragTransformRef.current;

      if (currentDragMode === 'none' || !currentDragStart || !currentInitialDragTransform) return;

      const cartesian = viewer.camera.pickEllipsoid(
        event.endPosition,
        viewer.scene.globe.ellipsoid
      );
      if (!cartesian) return;

      const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
      const currentLng = Cesium.Math.toDegrees(cartographic.longitude);
      const currentLat = Cesium.Math.toDegrees(cartographic.latitude);

      const startCartesian = viewer.camera.pickEllipsoid(
        new Cesium.Cartesian2(currentDragStart.x, currentDragStart.y),
        viewer.scene.globe.ellipsoid
      );
      if (!startCartesian) return;

      const startCartographic = Cesium.Cartographic.fromCartesian(startCartesian);
      const startLng = Cesium.Math.toDegrees(startCartographic.longitude);
      const startLat = Cesium.Math.toDegrees(startCartographic.latitude);

      if (currentDragMode === 'move') {
        // Move the anchor point
        const dLng = currentLng - startLng;
        const dLat = currentLat - startLat;

        setTransform(prev => ({
          ...prev,
          anchorLng: currentInitialDragTransform.anchorLng + dLng,
          anchorLat: currentInitialDragTransform.anchorLat + dLat,
        }));
      } else if (currentDragMode.startsWith('scale-')) {
        // Scale from the opposite corner
        const centerLng = currentInitialDragTransform.anchorLng;
        const centerLat = currentInitialDragTransform.anchorLat;

        // Calculate distance from center
        const metersPerDegreeLat = 111320;
        const metersPerDegreeLng = 111320 * Math.cos(centerLat * Math.PI / 180);

        const startDx = (startLng - centerLng) * metersPerDegreeLng;
        const startDy = (startLat - centerLat) * metersPerDegreeLat;
        const startDist = Math.sqrt(startDx * startDx + startDy * startDy);

        const currentDx = (currentLng - centerLng) * metersPerDegreeLng;
        const currentDy = (currentLat - centerLat) * metersPerDegreeLat;
        const currentDist = Math.sqrt(currentDx * currentDx + currentDy * currentDy);

        if (startDist > 0) {
          const scaleFactor = currentDist / startDist;
          setTransform(prev => ({
            ...prev,
            scaleX: currentInitialDragTransform.scaleX * scaleFactor,
            scaleY: currentInitialDragTransform.scaleY * scaleFactor,
          }));
        }
      } else if (currentDragMode === 'rotate') {
        // Rotate around center
        const centerLng = currentInitialDragTransform.anchorLng;
        const centerLat = currentInitialDragTransform.anchorLat;

        const startAngle = Math.atan2(startLat - centerLat, startLng - centerLng);
        const currentAngle = Math.atan2(currentLat - centerLat, currentLng - centerLng);

        const deltaAngle = Cesium.Math.toDegrees(currentAngle - startAngle);
        setTransform(prev => ({
          ...prev,
          rotation: currentInitialDragTransform.rotation - deltaAngle,
        }));
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    // Mouse up - end drag
    handler.setInputAction(() => {
      setDragMode('none');
      setDragStart(null);
      setInitialDragTransform(null);
    }, Cesium.ScreenSpaceEventType.LEFT_UP);

    return () => {
      // Cleanup
      if (overlayEntityRef.current && !viewer.isDestroyed()) {
        viewer.entities.remove(overlayEntityRef.current);
      }
      handleEntitiesRef.current.forEach(e => {
        if (!viewer.isDestroyed()) viewer.entities.remove(e);
      });
      handler.destroy();
    };
  }, [viewer]);

  // Update overlay when transform changes
  useEffect(() => {
    updateOverlay();
  }, [transform, updateOverlay]);

  // Handle confirm
  const handleConfirm = useCallback(() => {
    // Cleanup entities
    if (overlayEntityRef.current && !viewer.isDestroyed()) {
      viewer.entities.remove(overlayEntityRef.current);
    }
    handleEntitiesRef.current.forEach(e => {
      if (!viewer.isDestroyed()) viewer.entities.remove(e);
    });

    onConfirm(transform);
  }, [viewer, transform, onConfirm]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    // Cleanup entities
    if (overlayEntityRef.current && !viewer.isDestroyed()) {
      viewer.entities.remove(overlayEntityRef.current);
    }
    handleEntitiesRef.current.forEach(e => {
      if (!viewer.isDestroyed()) viewer.entities.remove(e);
    });

    onCancel();
  }, [viewer, onCancel]);

  // Fly to the floor plan bounds
  const flyToFloorPlan = useCallback(() => {
    if (!viewer || viewer.isDestroyed()) return;

    const bounds = calculateBounds(transform);
    viewer.camera.flyTo({
      destination: Cesium.Rectangle.fromDegrees(
        bounds.west,
        bounds.south,
        bounds.east,
        bounds.north
      ),
      duration: 1,
    });
  }, [viewer, transform, calculateBounds]);

  // Reset transform to initial
  const handleReset = useCallback(() => {
    setTransform(initialTransform);
  }, [initialTransform]);

  return (
    <div className="floor-plan-aligner">
      <div className="aligner-toolbar">
        <div className="aligner-title">Align Floor Plan</div>
        <div className="aligner-instructions">
          Drag center to move, corners to scale, rotation handle to rotate
        </div>
      </div>

      <div className="aligner-controls">
        <div className="aligner-input-group">
          <label>Scale (m/px)</label>
          <input
            type="number"
            value={transform.scaleX.toFixed(4)}
            onChange={(e) => {
              const val = parseFloat(e.target.value) || 0.1;
              setTransform(prev => ({ ...prev, scaleX: val, scaleY: val }));
            }}
            step="0.01"
            min="0.001"
          />
        </div>

        <div className="aligner-input-group">
          <label>Rotation</label>
          <input
            type="number"
            value={transform.rotation.toFixed(1)}
            onChange={(e) => {
              const val = parseFloat(e.target.value) || 0;
              setTransform(prev => ({ ...prev, rotation: val }));
            }}
            step="1"
          />
          <span>deg</span>
        </div>

        <button className="aligner-btn secondary" onClick={flyToFloorPlan}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
            <path d="M11 8v6M8 11h6" />
          </svg>
          Zoom
        </button>

        <button className="aligner-btn secondary" onClick={handleReset}>
          Reset
        </button>
      </div>

      <div className="aligner-actions">
        <button className="aligner-btn cancel" onClick={handleCancel}>
          Cancel
        </button>
        <button className="aligner-btn confirm" onClick={handleConfirm}>
          Confirm Alignment
        </button>
      </div>
    </div>
  );
}
